/**
 * A confirm dialog that takes arbitrary CONTENT, not just a message.
 *
 * `ConfirmDialog` accepts a plain string and binds Enter to confirm.
 * Several operator decisions need real controls inside the dialog --
 * a queued-task policy, a recommission checklist, a door-override
 * attestation -- and every one of them is a decision to be READ before
 * it is made, so Enter is deliberately NOT bound here: it would stand
 * a robot down, or declare a closed door open, mid-thought.
 *
 * Reuses `ConfirmDialog.css` and its `data-nx-modal-layer` contract
 * rather than restyling a second look. That attribute is load-bearing,
 * not decoration: the dialog portals into `document.body`, so it is
 * never inside the ref of the drawer that opened it, and without the
 * marker every drawer using `useDismissOnOutside` treats a press on
 * this dialog's own Confirm button as an OUTSIDE press -- unmounting
 * the drawer (and this dialog, its child) on `mousedown`, so the
 * `click` that would have fired `onConfirm` is never delivered. That
 * silently broke every door and lift command once already.
 *
 * EXTRACTED from `Fleet/RobotDetailDrawer.tsx`'s `CommissionDialog`
 * (2026-09-01) when the door-override attestation needed the same
 * thing. The default test ids are that component's, so its existing
 * tests still address it by the names they always used.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { DIALOG_DISABLED_BTN } from './dialog-styles';
import './ConfirmDialog.css';

export function FormDialog({
  title,
  children,
  confirmLabel,
  cancelLabel = 'Cancel',
  variant,
  confirmDisabled,
  onConfirm,
  onCancel,
  testId,
  cancelTestId = 'commission-dialog-cancel',
  confirmTestId = 'commission-dialog-confirm',
}: {
  title: string;
  children: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  variant: 'primary' | 'warning' | 'danger';
  confirmDisabled: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  testId: string;
  cancelTestId?: string;
  confirmTestId?: string;
}) {
  // `ConfirmDialog` moves focus onto its confirm button on open. This
  // one cannot copy that verbatim -- a gated confirm starts DISABLED
  // behind its checklist, and a disabled button takes no focus -- so
  // focus lands on the dialog container instead. Without it, focus
  // stays on the drawer button behind the modal and a keyboard user
  // tabs through the drawer, not the dialog they just opened.
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  // ESCAPE CLOSES IT. `ConfirmDialog` has bound this since it shipped;
  // this dialog never did, and appeared to only because the drawer
  // that opened it runs `useDismissOnOutside` -- so Escape unmounted
  // the DRAWER and took the dialog down with it. Opened from anywhere
  // else, the only exits were the Cancel button and a backdrop click,
  // and on a phone the backdrop is a sliver.
  //
  // Deliberately NOT Enter, which stays unbound for the reason in this
  // file's header: this dialog's confirm is gated behind a checklist,
  // and a stray Enter is exactly the press that should not carry it.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return createPortal(
    <div
      className="nx-confirm-dialog__backdrop"
      data-nx-modal-layer=""
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className={`nx-confirm-dialog nx-confirm-dialog--${variant}`}
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        data-testid={testId}
      >
        <h2 className="nx-confirm-dialog__title">{title}</h2>
        {children}
        <div className="nx-confirm-dialog__actions">
          <button
            type="button"
            className="nx-confirm-dialog__btn nx-confirm-dialog__btn--cancel"
            onClick={onCancel}
            data-testid={cancelTestId}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`nx-confirm-dialog__btn nx-confirm-dialog__btn--${variant}`}
            onClick={onConfirm}
            disabled={confirmDisabled}
            style={confirmDisabled ? DIALOG_DISABLED_BTN : undefined}
            data-testid={confirmTestId}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
